import React, { useState, useEffect } from 'react';
import { csrfFetch } from '../api/csrf';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function MLErrorClassification({ errors, fileName, autoTrigger = false }) {
  const [classifications, setClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoTriggered, setAutoTriggered] = useState(false);

  const classifyErrors = async () => {
    if (!errors || errors.length === 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await csrfFetch(`${API_BASE}/upload/ml/classify-errors`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          errors: errors,
          file_name: fileName,
          features: {}
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setClassifications(data.classifiedErrors || []);
      } else {
        setError(data.message || 'Failed to classify errors');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getErrorTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'missing data': return 'text-red-600 bg-red-100';
      case 'data format': return 'text-yellow-600 bg-yellow-100';
      case 'duplicate data': return 'text-orange-600 bg-orange-100';
      case 'timeliness': return 'text-purple-600 bg-purple-100';
      case 'data quality': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Auto-classify errors when component mounts
  useEffect(() => {
    if (errors && errors.length > 0) {
      if (autoTrigger && !autoTriggered) {
        classifyErrors();
        setAutoTriggered(true);
      } else if (!autoTrigger) {
        classifyErrors();
      }
    }
  }, [errors, fileName, autoTrigger]);

  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800">
          🤖 AI Error Classification
        </h3>
        <button
          onClick={classifyErrors}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {loading ? 'Classifying...' : 'Re-classify Errors'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {classifications.length > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Error Type Distribution
              </label>
              <div className="space-y-2">
                {Object.entries(
                  classifications.reduce((acc, error) => {
                    acc[error.predictedErrorType] = (acc[error.predictedErrorType] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getErrorTypeColor(type)}`}>
                      {type}
                    </span>
                    <span className="text-sm text-gray-600">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Severity Distribution
              </label>
              <div className="space-y-2">
                {Object.entries(
                  classifications.reduce((acc, error) => {
                    acc[error.predictedSeverity] = (acc[error.predictedSeverity] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([severity, count]) => (
                  <div key={severity} className="flex items-center justify-between">
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(severity)}`}>
                      {severity}
                    </span>
                    <span className="text-sm text-gray-600">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Detailed Classifications</h4>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {classifications.slice(0, 10).map((error, index) => (
                <div key={index} className="bg-gray-50 rounded-md p-3 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-800">
                      Row {error.row || 'N/A'}, Field "{error.field || 'N/A'}"
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getErrorTypeColor(error.predictedErrorType)}`}>
                        {error.predictedErrorType}
                      </span>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(error.predictedSeverity)}`}>
                        {error.predictedSeverity}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs">{error.message}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">
                      Confidence: {((error.confidence_level || 0) * 100).toFixed(0)}%
                    </span>
                    <span className="text-xs text-gray-500 font-mono">
                      {error.model_version}
                    </span>
                  </div>
                </div>
              ))}
              {classifications.length > 10 && (
                <p className="text-xs text-gray-500 text-center py-2">
                  ... and {classifications.length - 10} more errors
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {!classifications.length && !loading && !error && (
        <div className="text-center py-4">
          <p className="text-gray-500 text-sm">
            Click "Re-classify Errors" to get AI-powered error type and severity predictions
          </p>
        </div>
      )}
    </div>
  );
}
